import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './DataTable';
import VendorSelect from './VendorSelect';
import SearchableSelect from './SearchableSelect';
import Loader from './Loader';
import QRCodeGenerator from './QRCodeGenerator';
import AssetBulkOperations from './AssetBulkOperations';
import FileUploadComponent from './FileUploadComponent';
import { FileUploadService } from '../services/FileUploadService';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import ReceiptService from '../services/ReceiptService';
import { formatDateForInputIST, formatDisplayDateIST, getTodayInputDateIST } from '../utils/dateUtils';
import './InventoryDisplay.css';
import './Timeline.css';

const API_URL = '/api';

function Assets() {
  const { hasPermission } = useAuth();
  const emptyFilters = {
    assetType: '',
    status: ''
  };
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    assetTypeId: '',
    vendorId: '',
    purchaseDate: '',
    purchasePrice: '',
    pricePerItem: '',
    quantity: '1',
    description: '',
    attachmentFileName: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeadModal, setShowDeadModal] = useState(false);
  const [selectedAssetForDead, setSelectedAssetForDead] = useState(null);
  const [deadItems, setDeadItems] = useState([{ saralAssetId: '', serialNumber: '', notes: '' }]);
  const [showDeadListModal, setShowDeadListModal] = useState(false);
  const [selectedAssetDeadItems, setSelectedAssetDeadItems] = useState([]);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedAssetAssignments, setSelectedAssetAssignments] = useState([]);
  const [showRepairsModal, setShowRepairsModal] = useState(false);
  const [selectedAssetRepairs, setSelectedAssetRepairs] = useState([]);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [assetTimeline, setAssetTimeline] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedAssetForQR, setSelectedAssetForQR] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showPurchaseEntryModal, setShowPurchaseEntryModal] = useState(false);
  const [selectedAssetForPurchase, setSelectedAssetForPurchase] = useState(null);
  const [editingPurchaseEntry, setEditingPurchaseEntry] = useState(null);
  const [purchaseEntryForm, setPurchaseEntryForm] = useState({
    vendorId: '',
    billNumber: '',
    quantity: '1',
    purchaseDate: getTodayInputDateIST(),
    purchasePrice: '',
    pricePerItem: '',
    description: '',
    attachmentFileName: ''
  });
  const [purchaseEntryFiles, setPurchaseEntryFiles] = useState([]);
  const [showPurchaseHistoryModal, setShowPurchaseHistoryModal] = useState(false);
  const [selectedAssetForPurchaseHistory, setSelectedAssetForPurchaseHistory] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loadingPurchaseHistory, setLoadingPurchaseHistory] = useState(false);
  const [reopenPurchaseHistoryAfterEntryModal, setReopenPurchaseHistoryAfterEntryModal] = useState(false);
  const [showPurchaseDeleteModal, setShowPurchaseDeleteModal] = useState(false);
  const [purchaseEntryToDelete, setPurchaseEntryToDelete] = useState(null);
  const [openPurchaseActionMenu, setOpenPurchaseActionMenu] = useState(null);
  const [purchaseActionMenuPosition, setPurchaseActionMenuPosition] = useState({ top: 0, left: 0 });
  const [filters, setFilters] = useState(emptyFilters);
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    document.title = 'Assets - Saral Asset Manager';
    let isMounted = true;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [assetsResponse, assetTypesResponse] = await Promise.all([
          axios.get(`${API_URL}/assets`),
          axios.get(`${API_URL}/assettypes`)
        ]);

        if (!isMounted) return;
        setAssets(assetsResponse.data || []);
        setAssetTypes(assetTypesResponse.data || []);
      } catch (error) {
        console.error('Error fetching assets or asset types:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = () => setOpenPurchaseActionMenu(null);
    if (openPurchaseActionMenu !== null) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [openPurchaseActionMenu]);

  useEffect(() => {
    if (!showPurchaseHistoryModal) {
      setOpenPurchaseActionMenu(null);
    }
  }, [showPurchaseHistoryModal]);

  const getResolvedPurchasePrice = (quantityValue, totalPriceValue, unitPriceValue) => {
    const explicitTotal = parseFloat(totalPriceValue);
    if (!Number.isNaN(explicitTotal)) {
      return explicitTotal;
    }

    const quantity = parseInt(quantityValue, 10);
    const unitPrice = parseFloat(unitPriceValue);
    if (!Number.isNaN(quantity) && !Number.isNaN(unitPrice)) {
      return Number((quantity * unitPrice).toFixed(2));
    }

    return 0;
  };

  const handlePurchaseActionMenuToggle = (event, menuKey) => {
    event.stopPropagation();

    if (openPurchaseActionMenu === menuKey) {
      setOpenPurchaseActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const viewportPadding = 8;
    const preferredHeight = 180;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const openAbove = spaceBelow < 150;
    const maxHeight = openAbove
      ? Math.max(120, rect.top - (viewportPadding * 2))
      : Math.max(120, window.innerHeight - rect.bottom - (viewportPadding * 2));

    setPurchaseActionMenuPosition({
      top: openAbove
        ? Math.max(viewportPadding, rect.top - Math.min(preferredHeight, maxHeight))
        : Math.min(window.innerHeight - viewportPadding, rect.bottom + 4),
      left: Math.min(
        window.innerWidth - menuWidth - viewportPadding,
        Math.max(viewportPadding, rect.right - menuWidth)
      ),
      minWidth: menuWidth,
      maxHeight
    });
    setOpenPurchaseActionMenu(menuKey);
  };

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/assets`);
      const assets = response.data || [];
      setAssets(assets);
      return assets;
    } catch (error) {
      console.error('Error fetching assets:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/assettypes`);
      setAssetTypes(response.data);
    } catch (error) {
      console.error('Error fetching asset types:', error);
    }
  };

  const resetPurchaseEntryForm = () => {
    setPurchaseEntryForm({
      vendorId: '',
      billNumber: '',
      quantity: '1',
      purchaseDate: getTodayInputDateIST(),
      purchasePrice: '',
      pricePerItem: '',
      description: '',
      attachmentFileName: ''
    });
    setEditingPurchaseEntry(null);
    setSelectedAssetForPurchase(null);
    setPurchaseEntryFiles([]);
  };

  const closePurchaseEntryModal = (options = {}) => {
    const { preserveReturnState = false } = options;
    setShowPurchaseEntryModal(false);
    if (!preserveReturnState) {
      setReopenPurchaseHistoryAfterEntryModal(false);
    }
    resetPurchaseEntryForm();
  };

  const openAddAssetsModal = (asset, options = {}) => {
    const { fromPurchaseHistory = false } = options;
    setSelectedAssetForPurchase(asset);
    setEditingPurchaseEntry(null);
    setPurchaseEntryFiles([]);
    setReopenPurchaseHistoryAfterEntryModal(fromPurchaseHistory);
    if (fromPurchaseHistory) {
      setShowPurchaseHistoryModal(false);
    }
    setPurchaseEntryForm({
      vendorId: asset.vendorId ? String(asset.vendorId) : '',
      billNumber: '',
      quantity: '1',
      purchaseDate: getTodayInputDateIST(),
      purchasePrice: '',
      pricePerItem: '',
      description: '',
      attachmentFileName: ''
    });
    setShowPurchaseEntryModal(true);
  };

  const fetchAssetPurchaseHistory = async (assetId, options = {}) => {
    const { showModal = false, assetOverride = null } = options;
    setLoadingPurchaseHistory(true);
    try {
      const response = await axios.get(`${API_URL}/assets/${assetId}/purchases`);
      const history = response.data || [];
      setPurchaseHistory(history);
      if (showModal) {
        const asset = assetOverride || assets.find((item) => item.id === assetId) || null;
        setSelectedAssetForPurchaseHistory(asset);
        setShowPurchaseHistoryModal(true);
      }
      return history;
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      showAlert('danger', 'Failed to load purchase history.');
      return [];
    } finally {
      setLoadingPurchaseHistory(false);
    }
  };

  const openPurchaseHistoryModal = async (asset) => {
    await fetchAssetPurchaseHistory(asset.id, { showModal: true, assetOverride: asset });
  };

  const openEditPurchaseEntry = (purchaseEntry) => {
    const asset = assets.find((item) => item.id === purchaseEntry.assetId)
      || selectedAssetForPurchaseHistory
      || null;

    setSelectedAssetForPurchase(asset);
    setEditingPurchaseEntry(purchaseEntry);
    setPurchaseEntryFiles([]);
    setReopenPurchaseHistoryAfterEntryModal(showPurchaseHistoryModal);
    if (showPurchaseHistoryModal) {
      setShowPurchaseHistoryModal(false);
    }
    setPurchaseEntryForm({
      vendorId: purchaseEntry.vendorId ? String(purchaseEntry.vendorId) : '',
      billNumber: purchaseEntry.billNumber || '',
      quantity: String(purchaseEntry.quantity || 1),
      purchaseDate: formatDateForInputIST(purchaseEntry.purchaseDate) || getTodayInputDateIST(),
      purchasePrice: String(purchaseEntry.purchasePrice ?? ''),
      pricePerItem: String(purchaseEntry.pricePerItem ?? ''),
      description: purchaseEntry.description || '',
      attachmentFileName: purchaseEntry.attachmentFileName || ''
    });
    setShowPurchaseEntryModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const uploadFilesForAsset = async (assetId) => {
        if (selectedFiles.length === 0 || !assetId) return '';

        try {
          return await FileUploadService.uploadFiles(selectedFiles, `asset_${assetId}`);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          alert('File upload failed. Continuing without attachments.');
          return '';
        }
      };

      const assetData = {
        ...formData,
        assetTypeId: parseInt(formData.assetTypeId, 10),
        vendorId: formData.vendorId ? parseInt(formData.vendorId, 10) : null,
        purchasePrice: getResolvedPurchasePrice(formData.quantity, formData.purchasePrice, formData.pricePerItem),
        pricePerItem: parseFloat(formData.pricePerItem) || 0,
        quantity: parseInt(formData.quantity, 10),
        attachmentFileName: editingId ? formData.attachmentFileName : ''
      };

      if (editingId) {
        const uploadedFileNames = await uploadFilesForAsset(editingId);
        await axios.put(`${API_URL}/assets/${editingId}`, {
          ...assetData,
          id: editingId,
          attachmentFileName: uploadedFileNames || formData.attachmentFileName
        });
        handleCloseModal();
        showAlert('success', 'Asset updated successfully!');
      } else {
        const createResponse = await axios.post(`${API_URL}/assets`, assetData);
        const savedAsset = createResponse?.data;
        const savedAssetId = savedAsset?.id;

        if (savedAssetId) {
          const uploadedFileNames = await uploadFilesForAsset(savedAssetId);
          if (uploadedFileNames) {
            await axios.put(`${API_URL}/assets/${savedAssetId}`, {
              ...assetData,
              id: savedAssetId,
              attachmentFileName: uploadedFileNames
            });
          }
        }

        handleCloseModal();
        showAlert('success', savedAsset?.quantity > assetData.quantity ? 'Asset merged successfully!' : 'Asset added successfully!');
      }
      fetchAssets();
    } catch (error) {
      console.error('Error saving asset:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to save asset';
      showAlert('danger', `Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePurchaseEntrySubmit = async (e) => {
    e.preventDefault();
    if (!selectedAssetForPurchase || saving) return;

    setSaving(true);
    try {
      const uploadFilesForAsset = async (assetId) => {
        if (purchaseEntryFiles.length === 0 || !assetId) return '';

        try {
          return await FileUploadService.uploadFiles(purchaseEntryFiles, `asset_${assetId}`);
        } catch (uploadError) {
          console.error('Purchase file upload failed:', uploadError);
          showAlert('danger', 'Bill upload failed. Purchase entry was saved without new files.');
          return '';
        }
      };

      const payload = {
        name: selectedAssetForPurchase.name,
        assetTypeId: selectedAssetForPurchase.assetTypeId,
        vendorId: purchaseEntryForm.vendorId ? parseInt(purchaseEntryForm.vendorId, 10) : null,
        billNumber: purchaseEntryForm.billNumber,
        quantity: parseInt(purchaseEntryForm.quantity, 10),
        purchaseDate: purchaseEntryForm.purchaseDate,
        purchasePrice: getResolvedPurchasePrice(
          purchaseEntryForm.quantity,
          purchaseEntryForm.purchasePrice,
          purchaseEntryForm.pricePerItem
        ),
        pricePerItem: parseFloat(purchaseEntryForm.pricePerItem) || 0,
        description: purchaseEntryForm.description || null,
        attachmentFileName: purchaseEntryForm.attachmentFileName || null
      };

      if (editingPurchaseEntry) {
        const uploadedFileNames = await uploadFilesForAsset(selectedAssetForPurchase.id);
        await axios.put(`${API_URL}/assets/purchases/${editingPurchaseEntry.id}`, {
          ...payload,
          attachmentFileName: uploadedFileNames || purchaseEntryForm.attachmentFileName || null
        });
        showAlert('success', 'Purchase entry updated successfully!');
      } else {
        const createResponse = await axios.post(`${API_URL}/assets/${selectedAssetForPurchase.id}/purchases`, {
          ...payload,
          attachmentFileName: null
        });

        const savedPurchaseEntry = createResponse?.data;
        if (savedPurchaseEntry?.id) {
          const uploadedFileNames = await uploadFilesForAsset(selectedAssetForPurchase.id);
          if (uploadedFileNames) {
            await axios.put(`${API_URL}/assets/purchases/${savedPurchaseEntry.id}`, {
              ...payload,
              attachmentFileName: uploadedFileNames
            });
          }
        }

        showAlert('success', 'Assets added successfully!');
      }

      closePurchaseEntryModal({ preserveReturnState: true });
      const refreshedAssets = await fetchAssets();
      const refreshedAsset = refreshedAssets.find((item) => item.id === selectedAssetForPurchase.id) || selectedAssetForPurchase;
      if (reopenPurchaseHistoryAfterEntryModal) {
        await fetchAssetPurchaseHistory(selectedAssetForPurchase.id, { showModal: true, assetOverride: refreshedAsset });
        setReopenPurchaseHistoryAfterEntryModal(false);
      }
    } catch (error) {
      console.error('Error saving purchase entry:', error);
      const errorMessage = error.response?.data || error.message || 'Failed to save purchase entry.';
      showAlert('danger', String(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      assetTypeId: '',
      vendorId: '',
      purchaseDate: '',
      purchasePrice: '',
      pricePerItem: '',
      quantity: '1',
      description: '',
      attachmentFileName: ''
    });
    setEditingId(null);
    setSelectedFiles([]);
    setShowModal(true);
  };

  const handleEdit = (asset) => {
    setFormData({
      name: asset.name,
      assetTypeId: asset.assetTypeId,
      vendorId: asset.vendorId || '',
      purchaseDate: asset.purchaseDate.split('T')[0],
      purchasePrice: asset.purchasePrice,
      pricePerItem: asset.pricePerItem ?? '',
      quantity: asset.quantity || '1',
      description: asset.description || '',
      attachmentFileName: asset.attachmentFileName || ''
    });
    setEditingId(asset.id);
    setSelectedFiles([]);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeletePurchaseEntry = (purchaseEntry) => {
    setPurchaseEntryToDelete(purchaseEntry);
    setShowPurchaseDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/assets/${deleteId}`);
      setShowDeleteModal(false);
      setDeleteId(null);
      showAlert('success', 'Asset deleted successfully!');
      fetchAssets();
    } catch (error) {
      console.error('Error deleting asset:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to delete asset';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const confirmDeletePurchaseEntry = async () => {
    if (!purchaseEntryToDelete) return;

    try {
      await axios.delete(`${API_URL}/assets/purchases/${purchaseEntryToDelete.id}`);
      setShowPurchaseDeleteModal(false);
      setPurchaseEntryToDelete(null);
      showAlert('success', 'Purchase entry deleted successfully!');
      const refreshedAssets = await fetchAssets();
      const refreshedAsset = refreshedAssets.find((item) => item.id === purchaseEntryToDelete.assetId)
        || selectedAssetForPurchaseHistory;
      await fetchAssetPurchaseHistory(purchaseEntryToDelete.assetId, {
        showModal: true,
        assetOverride: refreshedAsset
      });
    } catch (error) {
      console.error('Error deleting purchase entry:', error);
      const errorMessage = error.response?.data || error.message || 'Failed to delete purchase entry.';
      showAlert('danger', String(errorMessage));
    }
  };

  const handlePrintPurchaseReceipt = async (purchaseEntry) => {
    try {
      await ReceiptService.generatePurchaseReceipt(purchaseEntry.id);
    } catch (error) {
      console.error('Error generating purchase receipt:', error);
      const errorMessage = error.response?.data || error.message || 'Failed to generate purchase receipt.';
      showAlert('danger', String(errorMessage));
    }
  };

  const handleOpenDeadModal = (asset) => {
    setSelectedAssetForDead(asset);
    setDeadItems([{ saralAssetId: '', serialNumber: '', notes: '' }]);
    setShowDeadModal(true);
  };

  const handleSubmitDeadItems = async (e) => {
    e.preventDefault();

    const items = deadItems
      .map(item => ({
        saralAssetId: item.saralAssetId.trim(),
        serialNumber: item.serialNumber.trim(),
        notes: item.notes.trim()
      }))
      .filter(item => item.serialNumber);

    if (!selectedAssetForDead || items.length === 0) {
      showAlert('danger', 'Add at least one serial number.');
      return;
    }

    try {
      await axios.post(`${API_URL}/assets/${selectedAssetForDead.id}/dead-items`, { items });
      setShowDeadModal(false);
      setSelectedAssetForDead(null);
      setDeadItems([{ saralAssetId: '', serialNumber: '', notes: '' }]);
      showAlert('success', `${items.length} asset unit(s) marked dead successfully!`);
      fetchAssets();
    } catch (error) {
      console.error('Error marking asset units as dead:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to mark asset units as dead';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleViewDeadItems = async (asset) => {
    try {
      const response = await axios.get(`${API_URL}/assets/${asset.id}/dead-items`);
      setSelectedAssetForDead(asset);
      setSelectedAssetDeadItems(response.data || []);
      setShowDeadListModal(true);
    } catch (error) {
      console.error('Error fetching dead asset items:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to load dead asset items';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleRestoreDeadItem = async (deadItem) => {
    if (!selectedAssetForDead) return;
    const confirmRestore = window.confirm(`Restore serial "${deadItem.serialNumber}"?`);
    if (!confirmRestore) return;

    try {
      await axios.delete(`${API_URL}/assets/${selectedAssetForDead.id}/dead-items/${deadItem.id}`);
      showAlert('success', 'Dead asset unit restored successfully!');

      const refreshedAssets = await fetchAssets();
      const updatedAsset = refreshedAssets.find((item) => item.id === selectedAssetForDead.id);
      if (updatedAsset) {
        setSelectedAssetForDead(updatedAsset);
      }
      const response = await axios.get(`${API_URL}/assets/${selectedAssetForDead.id}/dead-items`);
      setSelectedAssetDeadItems(response.data || []);
    } catch (error) {
      console.error('Error restoring dead asset unit:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to restore dead asset unit';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      assetTypeId: '',
      vendorId: '',
      purchaseDate: '',
      purchasePrice: '',
      pricePerItem: '',
      quantity: '1',
      description: '',
      attachmentFileName: ''
    });
    setEditingId(null);
    setSelectedFiles([]);
  };

  const handleViewAssignments = async (assetId) => {
    try {
      const response = await axios.get(`${API_URL}/assetassignments`);
      const assetAssignments = response.data.filter(assignment => 
        assignment.assetId === assetId && assignment.isActive
      );
      setSelectedAssetAssignments(assetAssignments);
      setShowAssignmentsModal(true);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const handleViewRepairs = async (assetId, options = {}) => {
    const { inRepairOnly = false } = options;
    try {
      const response = await axios.get(`${API_URL}/repairs`);
      const assetRepairs = response.data.filter(repair =>
        repair.assetId === assetId &&
        (!inRepairOnly || repair.status === 'InRepair')
      );
      setSelectedAssetRepairs(assetRepairs);
      setShowRepairsModal(true);
    } catch (error) {
      console.error('Error fetching repairs:', error);
    }
  };

  const handleViewTimeline = async (assetId) => {
    try {
      const [assignmentsRes, repairsRes] = await Promise.all([
        axios.get(`${API_URL}/assetassignments`),
        axios.get(`${API_URL}/repairs`)
      ]);
      
      const assignments = assignmentsRes.data.filter(a => a.assetId === assetId);
      const repairs = repairsRes.data.filter(r => r.assetId === assetId);
      
      const timeline = [
        ...assignments.map(a => ({
          type: 'assignment',
          date: a.assignedDate,
          title: `Assigned to ${a.employee?.name}`,
          details: `Saral ID: ${a.saralAssetId}, Serial: ${a.serialNumber}`,
          status: a.isActive ? 'Active' : 'Returned',
          returnDate: a.returnDate
        })),
        ...repairs.map(r => ({
          type: 'repair',
          date: r.sentDate,
          title: `Sent for Repair`,
          details: `Issue: ${r.issueDescription}, Vendor: ${r.vendor?.name}`,
          status: r.returnDate ? 'Completed' : 'In Progress',
          returnDate: r.returnDate,
          cost: r.repairCost
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setAssetTimeline(timeline);
      setShowTimelineModal(true);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  const filteredAssets = assets.filter(asset => {
    if (filters.assetType && asset.assetTypeId != filters.assetType) return false;
    if (filters.status && asset.status !== filters.status) return false;
    return true;
  });

  const assetTypeOptions = [
    { value: '', label: 'All Types' },
    ...assetTypes.map(type => ({ value: type.id, label: type.name }))
  ];

  const assetStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'Available', label: 'Available' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'InRepair', label: 'In Repair' },
    { value: 'Dead', label: 'Dead' }
  ];

  const hasActiveQuickFilters = Boolean(
    filters.assetType ||
    filters.status
  );

  const quickFilterToolbar = (
    <div className="d-flex flex-nowrap align-items-center gap-2 h-100 w-100 grid-filter-toolbar assets-filter-toolbar">
      <SearchableSelect
        className="flex-grow-1 grid-filter-select grid-filter-select-wide"
        controlClassName="form-control-sm"
        options={assetTypeOptions}
        value={filters.assetType}
        placeholder="All Asset Types"
        onChange={(e) => setFilters({ ...filters, assetType: e.target.value })}
      />
      <SearchableSelect
        className="flex-grow-1 grid-filter-select"
        controlClassName="form-control-sm"
        options={assetStatusOptions}
        value={filters.status}
        placeholder="All Statuses"
        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
      />
      <button
        className="btn btn-sm btn-outline-secondary grid-filter-clear ms-auto"
        onClick={() => setFilters(emptyFilters)}
        disabled={!hasActiveQuickFilters}
      >
        <i className="fas fa-eraser me-1"></i>Clear
      </button>
    </div>
  );

  const columns = [
    { 
      field: 'name', 
      label: 'Name'
    },
    { 
      field: 'assetType', 
      label: 'Type',
      render: (value) => value?.name || 'N/A',
      sortValue: (value) => value?.name || ''
    },
    { 
      field: 'quantity', 
      label: 'Inventory',
      headerSubLabel: 'Available = Total - Assigned - Dead',
      render: (value, item) => {
        const assigned = item.assignedCount || 0;
        const dead = item.deadCount || 0;
        const remaining = value - assigned - dead;
        const inRepair = item.inRepairCount || 0;
        
        return (
          <div className="inventory-display">
            <div className="fw-bold mb-1">{value} Total</div>
            <div className="d-flex flex-wrap gap-1">
              <div
                className="inventory-item available"
                title={`${remaining} available unit(s)`}
              >
                <i className="fas fa-check-circle"></i> {remaining}
              </div>
              {assigned > 0 && (
                <div 
                  className="inventory-item assigned clickable" 
                  title={`View assigned units for ${item.name}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleViewAssignments(item.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleViewAssignments(item.id);
                    }
                  }}
                >
                  <i className="fas fa-user"></i> {assigned}
                </div>
              )}
              {item.repairCount > 0 && (
                <div 
                  className="inventory-item repairs clickable" 
                  title={`View repair records for ${item.name}`}
                  onClick={() => handleViewRepairs(item.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleViewRepairs(item.id);
                    }
                  }}
                >
                  <i className="fas fa-wrench"></i> {item.repairCount}
                </div>
              )}
              {inRepair > 0 && (
                <div
                  className="inventory-item in-repair clickable"
                  title={`View in-repair units for ${item.name}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleViewRepairs(item.id, { inRepairOnly: true })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleViewRepairs(item.id, { inRepairOnly: true });
                    }
                  }}
                >
                  <i className="fas fa-tools"></i> {inRepair}
                </div>
              )}
              {dead > 0 && (
                <div
                  className="inventory-item clickable"
                  style={{ backgroundColor: '#dc3545', color: 'white' }}
                  title={`View dead units for ${item.name}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleViewDeadItems(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleViewDeadItems(item);
                    }
                  }}
                >
                  <i className="fas fa-ban"></i> {dead}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      field: 'lastPurchaseDate',
      label: 'Last Purchase',
      render: (value, item) => formatDisplayDateIST(value || item.purchaseDate),
      sortValue: (value, item) => new Date(value || item.purchaseDate).getTime()
    },
    ...(hasPermission('canViewPricing') ? [{
      field: 'purchasePrice',
      label: 'Pricing',
      render: (value, item) => (
        <div>
          <div className="fw-semibold">₹{Number(item.pricePerItem || 0).toFixed(2)}/item</div>
          <div className="text-muted small">₹{Number(value || 0).toFixed(2)} total</div>
        </div>
      ),
      sortValue: (value) => Number(value || 0)
    }] : []),
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      
      <div className="page-header">
        <h1 className="page-title mb-0">Assets</h1>
        <div className="page-header-actions">
          <button className="btn btn-sm btn-outline-info" onClick={() => setShowBulkModal(true)}>
            <i className="fas fa-upload me-1"></i><span className="d-none d-sm-inline">Bulk Operations</span><span className="d-sm-none">Bulk</span>
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-1"></i><span className="d-none d-sm-inline">Add Asset</span><span className="d-sm-none">Add</span>
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <DataTable
            data={filteredAssets}
            columns={columns}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbarContent={quickFilterToolbar}
            customActions={(item, closeMenu) => (
              <>
                <li>
                  <button className="dropdown-item" onClick={() => { closeMenu(); openPurchaseHistoryModal(item); }}>
                    <i className="fas fa-history me-2"></i>Purchase History
                  </button>
                </li>
                {(item.quantity - (item.assignedCount || 0) - (item.deadCount || 0)) > 0 && (
                  <>
                    <li><hr className="dropdown-divider" /></li>
                    <li>
                      <button className="dropdown-item text-danger" onClick={() => { closeMenu(); handleOpenDeadModal(item); }}>
                        <i className="fas fa-ban me-2"></i>Mark Dead Units
                      </button>
                    </li>
                  </>
                )}
              </>
            )}
          />
        </div>
      </div>

      {/* Modal */}
      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} style={{backgroundColor: showModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-xl" style={{ maxWidth: '1400px' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-laptop me-2"></i>
                {editingId ? 'Edit Asset' : 'Add New Asset'}
              </h5>
              <button type="button" className="btn-close" onClick={handleCloseModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Asset Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter asset name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Asset Type</label>
                    <SearchableSelect
                      options={assetTypes.map(type => ({ value: type.id, label: type.name }))}
                      value={formData.assetTypeId}
                      onChange={(e) => setFormData({ ...formData, assetTypeId: e.target.value })}
                      placeholder="Select Asset Type"
                      required
                      onQuickAdd={async (name) => {
                        try {
                          const response = await axios.post('/api/assettypes', {
                            name: name,
                            description: `${name} for office use`
                          });
                          await fetchAssetTypes();
                          return { value: response.data.id, label: response.data.name };
                        } catch (error) {
                          console.error('Error adding asset type:', error);
                          throw error;
                        }
                      }}
                      addButtonText="Add Asset Type"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Vendor</label>
                    <VendorSelect
                      value={formData.vendorId}
                      onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Purchase Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.purchaseDate}
                      onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      placeholder="Enter quantity"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required
                    />
                  </div>
                  {!editingId && (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">Price Per Item (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          placeholder="Enter price per item"
                          value={formData.pricePerItem}
                          onChange={(e) => setFormData({ ...formData, pricePerItem: e.target.value })}
                          disabled={!hasPermission('canViewPricing')}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Total Purchase Price (₹)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          placeholder="Enter total price or auto-calculated"
                          value={formData.purchasePrice}
                          onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                          disabled={!hasPermission('canViewPricing')}
                        />
                      </div>
                    </>
                  )}
                  <div className="col-md-6">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    ></textarea>
                  </div>
                  <FileUploadComponent
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    existingFiles={editingId && formData.attachmentFileName ? formData.attachmentFileName : null}
                    folderName={editingId ? `asset_${editingId}` : null}
                    label="Upload Purchase Receipt/Bill (Optional)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving}>
                  {saving ? (
                    <Loader size="small" />
                  ) : (
                    <>
                      <i className="fas fa-save me-1"></i>
                      {editingId ? 'Update Asset' : 'Add Asset'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showPurchaseEntryModal ? 'show d-block' : ''}`} style={{backgroundColor: showPurchaseEntryModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-plus-square me-2"></i>
                {editingPurchaseEntry ? 'Edit Purchase Entry' : 'Add Assets'}
              </h5>
              <button type="button" className="btn-close" onClick={closePurchaseEntryModal}></button>
            </div>
            <form onSubmit={handlePurchaseEntrySubmit}>
              <div className="modal-body">
                {selectedAssetForPurchase && (
                  <div className="alert alert-light border">
                    <div><strong>Asset:</strong> {selectedAssetForPurchase.name}</div>
                    <div><strong>Type:</strong> {selectedAssetForPurchase.assetType?.name || assetTypes.find((type) => type.id === selectedAssetForPurchase.assetTypeId)?.name || '-'}</div>
                    <div><strong>Current Inventory:</strong> {selectedAssetForPurchase.quantity}</div>
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Vendor</label>
                    <VendorSelect
                      value={purchaseEntryForm.vendorId}
                      onChange={(e) => setPurchaseEntryForm({ ...purchaseEntryForm, vendorId: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Bill Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={purchaseEntryForm.billNumber}
                      onChange={(e) => setPurchaseEntryForm({ ...purchaseEntryForm, billNumber: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={purchaseEntryForm.quantity}
                      onChange={(e) => setPurchaseEntryForm({ ...purchaseEntryForm, quantity: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Purchase Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={purchaseEntryForm.purchaseDate}
                      onChange={(e) => setPurchaseEntryForm({ ...purchaseEntryForm, purchaseDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Price Per Item (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      value={purchaseEntryForm.pricePerItem}
                      onChange={(e) => setPurchaseEntryForm({ ...purchaseEntryForm, pricePerItem: e.target.value })}
                      disabled={!hasPermission('canViewPricing')}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Total Purchase Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      value={purchaseEntryForm.purchasePrice}
                      onChange={(e) => setPurchaseEntryForm({ ...purchaseEntryForm, purchasePrice: e.target.value })}
                      disabled={!hasPermission('canViewPricing')}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={purchaseEntryForm.description}
                      onChange={(e) => setPurchaseEntryForm({ ...purchaseEntryForm, description: e.target.value })}
                    ></textarea>
                  </div>
                  <FileUploadComponent
                    selectedFiles={purchaseEntryFiles}
                    setSelectedFiles={setPurchaseEntryFiles}
                    existingFiles={editingPurchaseEntry && purchaseEntryForm.attachmentFileName ? purchaseEntryForm.attachmentFileName : null}
                    folderName={selectedAssetForPurchase ? `asset_${selectedAssetForPurchase.id}` : null}
                    label="Upload Purchase Receipt/Bill (Optional)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closePurchaseEntryModal}>
                  Cancel
                </button>
                <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving}>
                  {saving ? <Loader size="small" /> : (
                    <>
                      <i className="fas fa-save me-1"></i>
                      {editingPurchaseEntry ? 'Update Entry' : 'Add Assets'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showPurchaseHistoryModal ? 'show d-block' : ''}`} style={{backgroundColor: showPurchaseHistoryModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-history me-2"></i>
                Purchase History{selectedAssetForPurchaseHistory ? ` - ${selectedAssetForPurchaseHistory.name}` : ''}
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowPurchaseHistoryModal(false)}></button>
            </div>
            <div className="modal-body">
              {selectedAssetForPurchaseHistory && (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div className="alert alert-light border mb-0 flex-grow-1">
                    <div><strong>Asset:</strong> {selectedAssetForPurchaseHistory.name}</div>
                    <div><strong>Current Inventory:</strong> {selectedAssetForPurchaseHistory.quantity}</div>
                    {hasPermission('canViewPricing') && (
                      <div><strong>Merged Pricing:</strong> ₹{Number(selectedAssetForPurchaseHistory.pricePerItem || 0).toFixed(2)} per item, ₹{Number(selectedAssetForPurchaseHistory.purchasePrice || 0).toFixed(2)} total</div>
                    )}
                  </div>
                  <button className="btn btn-primary" onClick={() => openAddAssetsModal(selectedAssetForPurchaseHistory, { fromPurchaseHistory: true })}>
                    <i className="fas fa-plus-square me-1"></i>Add Assets
                  </button>
                </div>
              )}

              {loadingPurchaseHistory ? (
                <Loader text="Loading purchase history..." size="medium" />
              ) : purchaseHistory.length === 0 ? (
                <p className="text-muted mb-0">No purchase history found for this asset.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Bill Number</th>
                        <th>Vendor</th>
                        <th>Quantity</th>
                        {hasPermission('canViewPricing') && <th>Per Item</th>}
                        {hasPermission('canViewPricing') && <th>Total</th>}
                        <th>Files</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseHistory.map((entry, index) => (
                        <tr key={entry.id}>
                          <td>{formatDisplayDateIST(entry.purchaseDate)}</td>
                          <td>{entry.billNumber || '-'}</td>
                          <td>{entry.vendor?.name || '-'}</td>
                          <td>{entry.quantity}</td>
                          {hasPermission('canViewPricing') && <td>₹{Number(entry.pricePerItem || 0).toFixed(2)}</td>}
                          {hasPermission('canViewPricing') && <td>₹{Number(entry.purchasePrice || 0).toFixed(2)}</td>}
                          <td>
                            {entry.attachmentFileName ? (
                              <button
                                className="btn btn-sm btn-outline-primary attachment-download-button"
                                onClick={() => FileUploadService.downloadFiles(`asset_${entry.assetId}`)}
                                title="Download files"
                                aria-label="Download files"
                              >
                                <i className="fas fa-download"></i>
                              </button>
                            ) : '-'}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={(e) => handlePurchaseActionMenuToggle(e, `purchase-${entry.id || index}`)}
                              aria-label="Purchase actions"
                            >
                              <i className="fas fa-ellipsis-v"></i>
                            </button>
                            {openPurchaseActionMenu === `purchase-${entry.id || index}` && createPortal(
                              <ul
                                className="dropdown-menu show portal-action-menu"
                                style={{
                                  position: 'fixed',
                                  top: purchaseActionMenuPosition.top,
                                  left: purchaseActionMenuPosition.left,
                                  minWidth: purchaseActionMenuPosition.minWidth,
                                  maxHeight: purchaseActionMenuPosition.maxHeight,
                                  overflowY: 'auto',
                                  zIndex: 3000
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setOpenPurchaseActionMenu(null);
                                      openEditPurchaseEntry(entry);
                                    }}
                                  >
                                    <i className="fas fa-edit me-2"></i>Edit
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setOpenPurchaseActionMenu(null);
                                      handlePrintPurchaseReceipt(entry);
                                    }}
                                  >
                                    <i className="fas fa-file-invoice me-2"></i>Receipt
                                  </button>
                                </li>
                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={() => {
                                      setOpenPurchaseActionMenu(null);
                                      handleDeletePurchaseEntry(entry);
                                    }}
                                  >
                                    <i className="fas fa-trash me-2"></i>Delete
                                  </button>
                                </li>
                              </ul>,
                              document.body
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPurchaseHistoryModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                Confirm Delete
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this asset? This action cannot be undone.
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                <i className="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showPurchaseDeleteModal ? 'show d-block' : ''}`} style={{backgroundColor: showPurchaseDeleteModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                Confirm Delete
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowPurchaseDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              {purchaseEntryToDelete ? (
                <>
                  <p className="mb-3">Are you sure you want to delete this purchase detail? This action cannot be undone.</p>
                  <div className="alert alert-light border mb-0">
                    <div><strong>Bill Number:</strong> {purchaseEntryToDelete.billNumber || '-'}</div>
                    <div><strong>Quantity:</strong> {purchaseEntryToDelete.quantity}</div>
                    <div><strong>Purchase Date:</strong> {formatDisplayDateIST(purchaseEntryToDelete.purchaseDate)}</div>
                    {hasPermission('canViewPricing') && (
                      <div><strong>Total Price:</strong> ₹{Number(purchaseEntryToDelete.purchasePrice || 0).toFixed(2)}</div>
                    )}
                  </div>
                  <p className="text-danger small mt-3 mb-0">Deleting this purchase detail will reduce the merged asset inventory totals.</p>
                </>
              ) : 'Are you sure you want to delete this purchase detail?'}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPurchaseDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDeletePurchaseEntry}>
                <i className="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showDeadModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeadModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-ban me-2 text-danger"></i>
                Mark Asset Units as Dead
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeadModal(false)}></button>
            </div>
            <form onSubmit={handleSubmitDeadItems}>
              <div className="modal-body">
                {selectedAssetForDead && (
                  <div className="alert alert-light border">
                    <strong>Asset:</strong> {selectedAssetForDead.name}
                    <br />
                    <strong>Available to mark dead:</strong> {selectedAssetForDead.quantity - (selectedAssetForDead.assignedCount || 0) - (selectedAssetForDead.deadCount || 0)}
                  </div>
                )}
                {deadItems.map((item, index) => (
                  <div key={index} className="row g-2 mb-3 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label">Saral Asset ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.saralAssetId}
                        onChange={(e) => {
                          const updated = [...deadItems];
                          updated[index].saralAssetId = e.target.value;
                          setDeadItems(updated);
                        }}
                        placeholder="Optional Saral asset ID"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Serial Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.serialNumber}
                        onChange={(e) => {
                          const updated = [...deadItems];
                          updated[index].serialNumber = e.target.value;
                          setDeadItems(updated);
                        }}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Notes</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.notes}
                        onChange={(e) => {
                          const updated = [...deadItems];
                          updated[index].notes = e.target.value;
                          setDeadItems(updated);
                        }}
                        placeholder="Optional reason/details"
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger w-100"
                        onClick={() => setDeadItems(deadItems.filter((_, itemIndex) => itemIndex !== index))}
                        disabled={deadItems.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setDeadItems([...deadItems, { saralAssetId: '', serialNumber: '', notes: '' }])}
                >
                  <i className="fas fa-plus me-1"></i>Add Another Unit
                </button>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeadModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-danger">
                  <i className="fas fa-ban me-1"></i>Mark Dead
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showDeadListModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeadListModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg dead-assets-modal-dialog">
          <div className="modal-content dead-assets-modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-list me-2 text-danger"></i>
                Dead Asset Units{selectedAssetForDead ? ` - ${selectedAssetForDead.name}` : ''}
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeadListModal(false)}></button>
            </div>
            <div className="modal-body dead-assets-modal-body">
              {selectedAssetDeadItems.length > 0 ? (
                <div className="table-responsive dead-assets-table-wrap">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Saral Asset ID</th>
                        <th>Serial Number</th>
                        <th>Notes</th>
                        <th>Marked Dead At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAssetDeadItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.saralAssetId || '-'}</td>
                          <td>{item.serialNumber}</td>
                          <td>{item.notes || '-'}</td>
                          <td>{new Date(item.markedDeadAt).toLocaleString()}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleRestoreDeadItem(item)}
                            >
                              <i className="fas fa-undo me-1"></i>Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0">No dead asset units found.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeadListModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments Modal */}
      <div className={`modal fade ${showAssignmentsModal ? 'show d-block' : ''}`} style={{backgroundColor: showAssignmentsModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-users me-2"></i>
                Assigned Items
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowAssignmentsModal(false)}></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {selectedAssetAssignments.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Saral Asset ID</th>
                        <th>Serial Number</th>
                        <th>Employee</th>
                        <th>Assigned Date</th>
                        <th>Delivery Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAssetAssignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td>{assignment.saralAssetId}</td>
                          <td>{assignment.serialNumber}</td>
                          <td>{assignment.employee?.name || 'N/A'}</td>
                          <td>{new Date(assignment.assignedDate).toLocaleDateString()}</td>
                          <td>
                            <span className={`badge ${
                              assignment.deliveryStatus === 0 ? 'bg-warning text-dark' :
                              assignment.deliveryStatus === 1 ? 'bg-info text-white' :
                              'bg-success text-white'
                            }`}>
                              {assignment.deliveryStatus === 0 ? 'Pending' :
                               assignment.deliveryStatus === 1 ? 'In Transit' : 'Delivered'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No active assignments found for this asset.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAssignmentsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Repairs Modal */}
      <div className={`modal fade ${showRepairsModal ? 'show d-block' : ''}`} style={{backgroundColor: showRepairsModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-wrench me-2"></i>
                Repair Records
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowRepairsModal(false)}></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {selectedAssetRepairs.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Issue</th>
                        <th>Repair Source</th>
                        <th>Vendor</th>
                        {hasPermission('canViewPricing') && <th>Cost</th>}
                        <th>Sent Date</th>
                        <th>Return Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAssetRepairs.map((repair) => (
                        <tr key={repair.id}>
                          <td>{repair.repairReason || repair.issueDescription || '-'}</td>
                          <td>
                            <span className="badge bg-info">
                              {repair.repairSource === 0 ? 'Office' :
                               repair.repairSource === 1 ? 'Employee Reported' : 'Employee Returned'}
                            </span>
                          </td>
                          <td>{repair.repairVendor || repair.vendor?.name || 'N/A'}</td>
                          {hasPermission('canViewPricing') && <td>₹{repair.repairCost || 0}</td>}
                          <td>{repair.repairDate ? new Date(repair.repairDate).toLocaleDateString() : '-'}</td>
                          <td>{repair.completedDate ? new Date(repair.completedDate).toLocaleDateString() : '-'}</td>
                          <td>
                            <span className={`badge ${repair.completedDate ? 'bg-success' : 'bg-warning text-dark'}`}>
                              {repair.completedDate ? 'Completed' : 'In Progress'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No repair records found for this asset.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowRepairsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Modal */}
      <div className={`modal fade ${showTimelineModal ? 'show d-block' : ''}`} style={{backgroundColor: showTimelineModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg timeline-modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-history me-2"></i>
                Asset Timeline
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowTimelineModal(false)}></button>
            </div>
            <div className="modal-body timeline-modal-body">
              {assetTimeline.length > 0 ? (
                <div className="timeline">
                  {assetTimeline.map((event, index) => (
                    <div key={index} className={`timeline-item ${event.type}`}>
                      <div className="timeline-marker">
                        <i className={`fas ${event.type === 'assignment' ? 'fa-user' : 'fa-wrench'}`}></i>
                      </div>
                      <div className="timeline-content">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1">{event.title}</h6>
                            <p className="text-muted small mb-1">{event.details}</p>
                            <small className="text-muted">
                              {new Date(event.date).toLocaleString()}
                              {event.returnDate && (
                                <span> - {new Date(event.returnDate).toLocaleString()}</span>
                              )}
                            </small>
                          </div>
                          <div className="text-end">
                            <span className={`badge ${event.status === 'Active' || event.status === 'In Progress' ? 'bg-warning' : 'bg-success'}`}>
                              {event.status}
                            </span>
                            {event.cost && hasPermission('canViewPricing') && (
                              <div className="text-muted small mt-1">₹{event.cost}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No timeline data available for this asset.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowTimelineModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      <div className={`modal fade ${showQRModal ? 'show d-block' : ''}`} style={{backgroundColor: showQRModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-qrcode me-2"></i>
                Asset QR Code
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowQRModal(false)}></button>
            </div>
            <div className="modal-body">
              {selectedAssetForQR && (
                <QRCodeGenerator asset={selectedAssetForQR} onClose={() => setShowQRModal(false)} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Operations Modal */}
      <AssetBulkOperations 
        show={showBulkModal} 
        onHide={() => setShowBulkModal(false)}
        onRefresh={fetchAssets}
      />
    </div>
  );
}

export default Assets;
